/**
 * Adventure Socket.IO Client
 *
 * Client-side Socket.IO connection for Adventure Mode (co-op PvE).
 * Connects to the /adventure namespace for real-time adventure sessions.
 */

import { io, Socket } from 'socket.io-client';
import { getAuth } from 'firebase/auth';
import type {
  AdventureServerToClientEvents,
  AdventureClientToServerEvents,
  AdventureMoveConfirmedPayload,
  AdventureEncounterPreviewPayload,
  AdventureBattleStartingPayload,
  AdventureRestAppliedPayload,
  AdventureBattleResultPayload,
  AdventureErrorPayload,
  AdventureDeployPartnerPlacedPayload,
  AdventureDeployPartnerRemovedPayload,
  AdventureDeployReadyStatePayload,
  AdventureDeployCombatStartingPayload,
  AdventureCombatStateUpdatePayload,
} from '@drawn-of-war/shared';

// =====================================================================
// TYPES
// =====================================================================

// Re-export the state payload type from shared events for external consumers
type AdventureStatePayload = Parameters<AdventureServerToClientEvents['adventure:state']>[0];

// Local aliases for the socket event maps
type AdventureClientEvents = AdventureClientToServerEvents;
type AdventureServerEvents = AdventureServerToClientEvents;

// =====================================================================
// SOCKET CLIENT
// =====================================================================

import { getApiUrl } from '../config/api';

/** @deprecated Use getApiUrl() from config/api */
function getSocketUrl(): string {
  return getApiUrl();
}

export class AdventureSocketClient {
  private socket: Socket<AdventureServerEvents, AdventureClientEvents> | null = null;
  private adventureId: string | null = null;
  private joinPromise: Promise<AdventureStatePayload | null> | null = null;

  // =====================================================================
  // CONNECTION
  // =====================================================================

  /**
   * Ensure socket is connected (idempotent)
   */
  private ensureConnection(authToken?: string): void {
    if (this.socket?.connected) {
      return;
    }

    if (this.socket) {
      console.log('[Adventure Socket] Existing socket disconnected, cleaning up...');
      // Full cleanup is OK here since we're destroying the socket entirely
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.joinPromise = null;
    }

    const socketUrl = `${getSocketUrl()}/adventure`;
    console.log('[Adventure Socket] Creating socket connection to:', socketUrl);

    this.socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      autoConnect: false,
      auth: {
        token: authToken || ''
      }
    });

    this.socket.on('connect', () => {
      console.log('[Adventure Socket] Connected! Socket ID:', this.socket?.id);
      if (this.adventureId) {
        this.joinAdventure(this.adventureId);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Adventure Socket] Disconnected. Reason:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Adventure Socket] Connection error:', error.message);
    });

    this.socket.io.on('reconnect_attempt', () => {
      console.log('[Adventure Socket] Attempting reconnect...');
    });

    console.log('[Adventure Socket] Initiating connection...');
    this.socket.connect();
  }

  /**
   * Connect to adventure socket and join adventure session
   */
  async connect(adventureId: string): Promise<AdventureStatePayload | null> {
    // If switching to a different adventure, fully disconnect first to avoid stale state
    if (this.adventureId && this.adventureId !== adventureId) {
      console.log('[Adventure Socket] Switching adventure from', this.adventureId, 'to', adventureId, '- disconnecting old session');
      this.disconnect();
    }

    this.adventureId = adventureId;

    if (this.joinPromise) {
      return this.joinPromise;
    }

    let authToken: string | undefined;
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        authToken = await user.getIdToken();
        console.log('[Adventure Socket] Got Firebase ID token for user:', user.uid);
      }
    } catch (error) {
      console.error('[Adventure Socket] Failed to get auth token:', error);
    }

    this.joinPromise = new Promise<AdventureStatePayload | null>((resolve, reject) => {
      this.ensureConnection(authToken);

      if (!this.socket) {
        reject(new Error('Failed to create socket'));
        return;
      }

      const timeout = setTimeout(() => {
        this.socket?.off('adventure:state', onState);
        this.socket?.off('adventure:error', onError);
        reject(new Error('Connection timeout'));
      }, 10000);

      const onState = (state: AdventureStatePayload) => {
        clearTimeout(timeout);
        this.socket?.off('adventure:state', onState);
        this.socket?.off('adventure:error', onError);
        console.log('[Adventure Socket] Successfully joined adventure');
        resolve(state);
      };

      const onError = (error: { code: string; message: string }) => {
        clearTimeout(timeout);
        this.socket?.off('adventure:state', onState);
        this.socket?.off('adventure:error', onError);
        console.error('[Adventure Socket] Join failed:', error.message);
        reject(new Error(error.message));
      };

      this.socket.once('adventure:state', onState);
      this.socket.once('adventure:error', onError);

      if (this.socket.connected) {
        this.joinAdventure(adventureId);
      }
    });

    return this.joinPromise;
  }

  /**
   * Disconnect from adventure socket
   */
  disconnect(): void {
    if (this.socket) {
      if (this.adventureId) {
        this.socket.emit('adventure:save-exit', { adventureId: this.adventureId });
      }
      this.socket.disconnect();
      this.socket = null;
      this.adventureId = null;
      this.joinPromise = null;
      console.log('[Adventure Socket] Disconnected');
    }
  }

  /**
   * Join an adventure session
   */
  private joinAdventure(adventureId: string): void {
    if (!this.socket) {
      console.error('[Adventure Socket] Not connected');
      return;
    }
    this.socket.emit('adventure:join', { adventureId });
    console.log('[Adventure Socket] Joining adventure:', adventureId);
  }

  // =====================================================================
  // ACTIONS
  // =====================================================================

  /**
   * Move to a target hex on the adventure map
   */
  move(targetHex: { q: number; r: number }): void {
    if (!this.socket || !this.adventureId) return;
    this.socket.emit('adventure:move', { adventureId: this.adventureId, targetHex });
  }

  /**
   * Enter battle at an encounter hex
   */
  enterBattle(encounterId: string): void {
    if (!this.socket || !this.adventureId) return;
    this.socket.emit('adventure:enter-battle', { adventureId: this.adventureId, encounterId });
  }

  /**
   * Confirm readiness for battle (co-op confirmation)
   */
  confirmBattle(encounterId: string): void {
    if (!this.socket || !this.adventureId) return;
    this.socket.emit('adventure:confirm-battle', { adventureId: this.adventureId, encounterId });
  }

  /**
   * Cancel pending battle
   */
  cancelBattle(encounterId: string): void {
    if (!this.socket || !this.adventureId) return;
    this.socket.emit('adventure:cancel-battle', { adventureId: this.adventureId, encounterId });
  }

  /**
   * Save progress and exit adventure
   */
  saveAndExit(): void {
    if (!this.socket || !this.adventureId) return;
    this.socket.emit('adventure:save-exit', { adventureId: this.adventureId });
  }

  /**
   * Choose what to do when partner disconnects
   */
  disconnectChoice(choice: 'continue_solo' | 'save_and_wait'): void {
    if (!this.socket || !this.adventureId) return;
    this.socket.emit('adventure:disconnect-choice', { adventureId: this.adventureId, choice });
  }

  /**
   * Place a creature on the deployment grid
   */
  deployPlace(encounterId: string, creatureId: string, position: { q: number; r: number }): void {
    if (!this.socket || !this.adventureId) {
      console.warn('[Adventure Socket] deployPlace skipped - no socket or adventureId');
      return;
    }
    this.socket.emit('adventure:deploy-place', { adventureId: this.adventureId, encounterId, creatureId, position });
  }

  /**
   * Remove a creature from the deployment grid
   */
  deployRemove(encounterId: string, creatureId: string): void {
    if (!this.socket || !this.adventureId) return;
    this.socket.emit('adventure:deploy-remove', { adventureId: this.adventureId, encounterId, creatureId });
  }

  /**
   * Mark self as ready for combat deployment.
   * Includes all placements to avoid race condition with deploy-place events.
   */
  deployReady(encounterId: string, placements?: Array<{ creatureId: string; position: { q: number; r: number } }>): void {
    if (!this.socket || !this.adventureId) {
      console.warn('[Adventure Socket] deployReady skipped - no socket or adventureId');
      return;
    }
    this.socket.emit('adventure:deploy-ready', { adventureId: this.adventureId, encounterId, placements });
  }

  /**
   * Unmark self as ready for combat deployment
   */
  deployUnready(encounterId: string): void {
    if (!this.socket || !this.adventureId) return;
    this.socket.emit('adventure:deploy-unready', { adventureId: this.adventureId, encounterId });
  }

  // =====================================================================
  // EVENT LISTENERS
  // =====================================================================

  /**
   * Listen for move confirmations
   */
  onMoveConfirmed(cb: (data: AdventureMoveConfirmedPayload) => void): void {
    this.socket?.on('adventure:move-confirmed', cb);
  }

  /**
   * Listen for move rejections
   */
  onMoveRejected(cb: (data: { reason: string }) => void): void {
    this.socket?.on('adventure:move-rejected', cb);
  }

  /**
   * Listen for encounter preview data when stepping on encounter hex
   */
  onEncounterPreview(cb: (data: AdventureEncounterPreviewPayload) => void): void {
    this.socket?.on('adventure:encounter-preview', cb);
  }

  /**
   * Listen for partner confirming battle readiness
   */
  onPartnerConfirmedBattle(cb: (data: { encounterId: string }) => void): void {
    this.socket?.on('adventure:partner-confirmed-battle', cb);
  }

  /**
   * Listen for battle starting (both players confirmed)
   */
  onBattleStarting(cb: (data: AdventureBattleStartingPayload) => void): void {
    this.socket?.on('adventure:battle-starting', cb);
  }

  /**
   * Listen for battle cancellation
   */
  onBattleCancelled(cb: (data: { encounterId: string; cancelledBy: string }) => void): void {
    this.socket?.on('adventure:battle-cancelled', cb);
  }

  /**
   * Listen for battle results
   */
  onBattleResult(cb: (data: AdventureBattleResultPayload) => void): void {
    this.socket?.on('adventure:battle-result', cb);
  }

  /**
   * Listen for rest point healing
   */
  onRestApplied(cb: (data: AdventureRestAppliedPayload) => void): void {
    this.socket?.on('adventure:rest-applied', cb);
  }

  /**
   * Listen for session pause (partner disconnected and chose save_and_wait)
   */
  onSessionPaused(cb: (data: { pausedBy: string }) => void): void {
    this.socket?.on('adventure:session-paused', cb);
  }

  /**
   * Listen for partner connecting to adventure
   */
  onPartnerConnected(cb: (data: { displayName: string }) => void): void {
    this.socket?.on('adventure:partner-connected', cb);
  }

  /**
   * Listen for partner disconnecting from adventure
   */
  onPartnerDisconnected(cb: (data: { displayName: string; options: string[] }) => void): void {
    this.socket?.on('adventure:partner-disconnected', cb);
  }

  /**
   * Listen for partner reconnecting to adventure
   */
  onPartnerReconnected(cb: (data: { displayName: string }) => void): void {
    this.socket?.on('adventure:partner-reconnected', cb);
  }

  /**
   * Listen for session resuming after pause
   */
  onResumed(cb: () => void): void {
    this.socket?.on('adventure:resumed', cb);
  }

  /**
   * Listen for errors
   */
  onError(cb: (data: { code: string; message: string }) => void): void {
    this.socket?.on('adventure:error', cb);
  }

  /**
   * Listen for partner placing a creature during deployment
   */
  onDeployPartnerPlaced(cb: (data: { creatureId: string; position: { q: number; r: number }; ownerId: string }) => void): void {
    this.socket?.on('adventure:deploy-partner-placed', cb);
  }

  /**
   * Listen for partner removing a creature during deployment
   */
  onDeployPartnerRemoved(cb: (data: { creatureId: string }) => void): void {
    this.socket?.on('adventure:deploy-partner-removed', cb);
  }

  /**
   * Listen for deployment ready state updates
   */
  onDeployReadyState(cb: (data: { readyCount: number; neededCount: number }) => void): void {
    this.socket?.on('adventure:deploy-ready-state', cb);
  }

  /**
   * Listen for deployment combat starting (all players ready)
   */
  onDeployCombatStarting(cb: (data: { matchId: string; battlefieldConfig: Record<string, unknown>; playerCreatures: unknown[]; enemyCreatures: unknown[] }) => void): void {
    this.socket?.on('adventure:deploy-combat-starting', cb);
  }

  // =====================================================================
  // COMBAT EVENT LISTENERS (used by DeploymentGridDemoPage adapter)
  // =====================================================================

  /**
   * Listen for real-time combat state updates during battle
   */
  onCombatStateUpdate(cb: (data: AdventureCombatStateUpdatePayload) => void): void {
    this.socket?.on('adventure:combat-state-update', cb);
  }

  /**
   * Listen for combat events (damage, deaths, abilities) during battle
   */
  onCombatEvents(cb: (data: { matchId: string; events: unknown[] }) => void): void {
    this.socket?.on('adventure:combat-events' as any, cb);
  }

  /**
   * Clear only combat-related listeners (state updates, events, results)
   * Used when transitioning between deployment and combat phases
   */
  clearCombatListeners(): void {
    this.socket?.off('adventure:combat-state-update');
    this.socket?.off('adventure:combat-events' as any);
    this.socket?.off('adventure:battle-result');
    console.log('[Adventure Socket] Cleared combat listeners');
  }

  // =====================================================================
  // CLEANUP
  // =====================================================================

  /**
   * Remove all application-level event listeners.
   * Delegates to clearAdventureListeners() which uses specific off() calls
   * to avoid removing internal Socket.IO handlers needed for reconnection.
   */
  clearAllListeners(): void {
    this.clearAdventureListeners();
  }

  /**
   * Clear all adventure-related listeners without affecting connection handlers
   * Call this before registering new listeners to prevent duplicates
   */
  clearAdventureListeners(): void {
    this.socket?.off('adventure:move-confirmed');
    this.socket?.off('adventure:move-rejected');
    this.socket?.off('adventure:encounter-preview');
    this.socket?.off('adventure:partner-confirmed-battle');
    this.socket?.off('adventure:battle-starting');
    this.socket?.off('adventure:battle-cancelled');
    this.socket?.off('adventure:battle-result');
    this.socket?.off('adventure:rest-applied');
    this.socket?.off('adventure:session-paused');
    this.socket?.off('adventure:partner-connected');
    this.socket?.off('adventure:partner-disconnected');
    this.socket?.off('adventure:partner-reconnected');
    this.socket?.off('adventure:resumed');
    this.socket?.off('adventure:error');
    this.socket?.off('adventure:deploy-partner-placed');
    this.socket?.off('adventure:deploy-partner-removed');
    this.socket?.off('adventure:deploy-ready-state');
    this.socket?.off('adventure:deploy-combat-starting');
    this.socket?.off('adventure:combat-state-update');
    this.socket?.off('adventure:combat-events' as any);
    console.log('[Adventure Socket] Cleared adventure listeners');
  }

  /**
   * Check if connected
   */
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current adventure ID
   */
  get currentAdventureId(): string | null {
    return this.adventureId;
  }
}

/**
 * Singleton instance
 */
export const adventureSocket = new AdventureSocketClient();

// Export payload types for use in stores and components
export type {
  AdventureStatePayload,
  AdventureMoveConfirmedPayload,
  AdventureEncounterPreviewPayload,
  AdventureBattleStartingPayload,
  AdventureRestAppliedPayload,
  AdventureBattleResultPayload,
  AdventureCombatStateUpdatePayload,
};
